import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Milk, ArrowLeft } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { cn } from "@/lib/utils";
import Footer from "@/components/Footer";

export function H2({ children }) {
  return <h2 className="mt-8 font-heading text-lg font-bold text-foreground">{children}</h2>;
}

export function P({ children }) {
  return <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{children}</p>;
}

export function UL({ items }) {
  return (
    <ul className="mt-3 space-y-2 pl-1">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-muted-foreground">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export function ContactCard({ lang = "en" }) {
  const L = lang === "mr"
    ? { heading: "संपर्क", brand: "व्यवसाय / ब्रँड:", owner: "व्यवसाय मालक:", email: "ईमेल:", address: "पत्ता:" }
    : { heading: "Contact", brand: "Business / Brand:", owner: "Business Owner:", email: "Email:", address: "Address:" };
  return (
    <div className="mt-8 rounded-xl border bg-muted/40 p-5">
      <p className="font-heading text-sm font-bold text-foreground">{L.heading}</p>
      <dl className="mt-3 space-y-1.5 text-sm text-muted-foreground">
        <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
          <dt className="font-medium text-foreground">{L.brand}</dt>
          <dd>Dairy Hub ERP</dd>
        </div>
        <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
          <dt className="font-medium text-foreground">{L.owner}</dt>
          <dd>Sunil Gangadhar Kamble</dd>
        </div>
        <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
          <dt className="font-medium text-foreground">{L.email}</dt>
          <dd>
            <a href="mailto:support@dairyhuberp.com" className="text-primary hover:underline">
              support@dairyhuberp.com
            </a>
          </dd>
        </div>
        <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
          <dt className="font-medium text-foreground">{L.address}</dt>
          <dd>At Post Andhari, Tq. Sillod, Dist. Sambhajinagar, Maharashtra, India</dd>
        </div>
      </dl>
    </div>
  );
}

export default function LegalLayout({ title, effectiveDate, testId, children }) {
  const { lang, setLang } = useI18n();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const backLabel = lang === "mr" ? "लॉगिनकडे परत" : "Back to Login";
  const effLabel = lang === "mr" ? "अंमलात येण्याची तारीख:" : "Effective Date:";

  return (
    <div className="flex min-h-screen flex-col bg-background" data-testid={testId}>
      {/* Header */}
      <header className="sticky top-0 z-30 border-b bg-card/80 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-3xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="legal-brand-home">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Milk className="h-5 w-5" strokeWidth={2} />
            </div>
            <span className="font-heading text-base font-bold">Dairy Hub ERP</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="flex items-center rounded-lg border p-0.5 text-xs" data-testid="legal-lang-toggle">
              <button
                type="button"
                onClick={() => setLang("en")}
                data-testid="legal-lang-en"
                className={cn(
                  "rounded-md px-2.5 py-1 font-semibold transition-colors",
                  lang === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                )}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => setLang("mr")}
                data-testid="legal-lang-mr"
                className={cn(
                  "rounded-md px-2.5 py-1 font-semibold transition-colors",
                  lang === "mr" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                )}
              >
                मराठी
              </button>
            </div>
            <Link
              to="/login"
              data-testid="legal-back-login"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
            >
              <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">{backLabel}</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <article className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            {title}
          </h1>
          {effectiveDate && (
            <p className="mt-2 text-sm font-medium text-muted-foreground">{effLabel} {effectiveDate}</p>
          )}
          <div className="mt-6">{children}</div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
